import { PageLayout } from '@/components/layout'
import { Footer } from '@/components/layout/Footer'
import { useSEO } from '@/hooks/useSEO'

export const TermsAndConditions = () => {
  useSEO({
    title: 'Terms and Conditions - Binder Tech Solutions',
    description: 'Terms and Conditions for using Binder Tech Solutions platform. Read our terms of service, user obligations, and platform rules.',
    keywords: 'terms and conditions, terms of service, user agreement, Binder Tech Solutions',
    url: 'https://bindertech.com/terms-and-conditions',
    type: 'website',
  })

  return (
    <PageLayout>
      <div className="mx-auto max-w-4xl px-4 py-12 md:px-10 lg:px-20">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-text-main-light dark:text-text-main-dark mb-4">
            Terms and Conditions
          </h1>
          <p className="text-text-sub-light dark:text-text-sub-dark">
            Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>

        <div className="prose prose-lg dark:prose-invert max-w-none">
          <div className="space-y-8 text-text-main-light dark:text-text-main-dark">
            {/* Section 1 */}
            <section>
              <h2 className="text-2xl font-semibold mb-4 text-text-main-light dark:text-text-main-dark">
                1. Definitions & Scope
              </h2>
              <ul className="list-disc pl-6 space-y-2 text-text-sub-light dark:text-text-sub-dark">
                <li>The application is a platform that provides the business owners with intelligent solution for spatial analysis and geofencing.</li>
                <li>Use of the application is governed by these Terms & Conditions.</li>
              </ul>
            </section>

            {/* Section 2 */}
            <section>
              <h2 className="text-2xl font-semibold mb-4 text-text-main-light dark:text-text-main-dark">
                2. Eligibility & Account Use
              </h2>
              <ul className="list-disc pl-6 space-y-2 text-text-sub-light dark:text-text-sub-dark">
                <li>Users must be 18 years or older to use the application.</li>
                <li>Registration is mandatory and must include accurate and complete information.</li>
                <li>Each user may register only one account per email and phone number.</li>
                <li>Accounts may be suspended or deleted for false information, duplicate accounts, or violations.</li>
                <li>Users whose accounts were previously terminated are not permitted to re-register.</li>
              </ul>
            </section>

            {/* Section 3 */}
            <section>
              <h2 className="text-2xl font-semibold mb-4 text-text-main-light dark:text-text-main-dark">
                3. Legal Compliance
              </h2>
              <ul className="list-disc pl-6 space-y-2 text-text-sub-light dark:text-text-sub-dark">
                <li>Users must comply with all applicable local laws, regulations, and e-commerce rules.</li>
                <li>Service providers must hold all required licenses and approvals to offer services.</li>
                <li>Any illegal use of the application is strictly prohibited.</li>
              </ul>
            </section>

            {/* Section 4 */}
            <section>
              <h2 className="text-2xl font-semibold mb-4 text-text-main-light dark:text-text-main-dark">
                4. Account Security & Responsibility
              </h2>
              <ul className="list-disc pl-6 space-y-2 text-text-sub-light dark:text-text-sub-dark">
                <li>Users are responsible for maintaining the confidentiality of login credentials.</li>
                <li>The application is not responsible for losses caused by unauthorized account access.</li>
                <li>Users must immediately report any suspected hacking or misuse.</li>
                <li>Users are liable for all activities conducted through their accounts.</li>
              </ul>
            </section>

            {/* Section 5 */}
            <section>
              <h2 className="text-2xl font-semibold mb-4 text-text-main-light dark:text-text-main-dark">
                5. User & Service Provider Obligations
              </h2>
              <ul className="list-disc pl-6 space-y-2 text-text-sub-light dark:text-text-sub-dark">
                <li>All information provided must be truthful, lawful, and non-misleading.</li>
                <li>Usernames, service descriptions, and content must not contain offensive or illegal material.</li>
                <li>Service providers confirm they have legal authority to offer listed services.</li>
                <li>Users may not share personal contact details or conduct transactions outside the application.</li>
              </ul>
            </section>

            {/* Section 6 */}
            <section>
              <h2 className="text-2xl font-semibold mb-4 text-text-main-light dark:text-text-main-dark">
                6. Communications
              </h2>
              <ul className="list-disc pl-6 space-y-2 text-text-sub-light dark:text-text-sub-dark">
                <li>Users agree to receive electronic communications, including notifications and promotions.</li>
                <li>Promotional emails may be unsubscribed from at any time.</li>
                <li>Electronic communications are considered legally valid.</li>
              </ul>
            </section>

            {/* Section 7 */}
            <section>
              <h2 className="text-2xl font-semibold mb-4 text-text-main-light dark:text-text-main-dark">
                7. Fees, Payments & Commissions
              </h2>
              <ul className="list-disc pl-6 space-y-2 text-text-sub-light dark:text-text-sub-dark">
                <li>Account registration and payment terms are discussed and agreed upon with the organization that owns the service.</li>
                <li>Registration may be free; however, transaction fees or commissions may apply based on the agreed terms.</li>
                <li>All fees are calculated in the platform's local currency.</li>
                <li>Fees may be modified by the platform with prior notification.</li>
                <li>Failure to pay applicable fees may result in account suspension, termination, or legal action.</li>
              </ul>
            </section>

            {/* Section 8 */}
            <section>
              <h2 className="text-2xl font-semibold mb-4 text-text-main-light dark:text-text-main-dark">
                8. Prohibited Content & Activities
              </h2>
              <p className="mb-3 text-text-sub-light dark:text-text-sub-dark">Users may not:</p>
              <ul className="list-disc pl-6 space-y-2 text-text-sub-light dark:text-text-sub-dark">
                <li>Post false, misleading, defamatory, obscene, or offensive content.</li>
                <li>Violate intellectual property rights or privacy of others.</li>
                <li>Conduct political, religiously offensive, or culturally inappropriate activities.</li>
                <li>Manipulate prices, transactions, or platform systems.</li>
                <li>Attempt fraud, hacking, data misuse, or malware distribution.</li>
                <li>Complete transactions outside the platform for listed services.</li>
              </ul>
            </section>

            {/* Section 9 */}
            <section>
              <h2 className="text-2xl font-semibold mb-4 text-text-main-light dark:text-text-main-dark">
                9. Intellectual Property
              </h2>
              <ul className="list-disc pl-6 space-y-2 text-text-sub-light dark:text-text-sub-dark">
                <li>All platform content (text, logos, software, designs) is protected by intellectual property laws.</li>
                <li>Users may not copy, distribute, or modify platform content without permission.</li>
                <li>Platform trademarks may not be used outside the application without authorization.</li>
              </ul>
            </section>

            {/* Section 10 */}
            <section>
              <h2 className="text-2xl font-semibold mb-4 text-text-main-light dark:text-text-main-dark">
                10. Modifications & Termination
              </h2>
              <ul className="list-disc pl-6 space-y-2 text-text-sub-light dark:text-text-sub-dark">
                <li>The platform may update Terms & Conditions at any time with notice.</li>
                <li>Continued use constitutes acceptance of updated terms.</li>
                <li>The platform may suspend or terminate accounts for violations without liability.</li>
              </ul>
            </section>
          </div>
        </div>
      </div>
      <Footer />
    </PageLayout>
  )
}

